import { Download } from "lucide-react";
import React, { useState, useCallback } from "react";
import { ActionIcon, Tooltip } from "@mantine/core";
import dayjs from "dayjs";
import { showNotification } from "@mantine/notifications";
import { SegmentationBlocksHistoryApiService } from "../../services/api/SegmentationBlocksHistoryApiService";
import { ISegmentationBlockHistoryEntry } from "../../@types/segmentationBlocks";

const CSV_HEADERS = ["Timestamp", "User", "Segment Name", "Status Update"];

const formatTimestamp = (timestamp: string | null | undefined): string => {
  if (!timestamp) return "-";
  const parsed = dayjs(timestamp);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : "-";
};

const escapeCSVField = (field: string): string => {
  if (field.includes(",") || field.includes("\n") || field.includes('"')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
};

const generateCSVContent = (
  entries: ISegmentationBlockHistoryEntry[]
): string => {
  const rows = [CSV_HEADERS.join(",")];

  entries.forEach((entry) => {
    const row = [
      escapeCSVField(formatTimestamp(entry.timestamp)),
      escapeCSVField(entry.actor_name),
      escapeCSVField(entry.segment_name),
      escapeCSVField(entry.action_label),
    ].join(",");
    rows.push(row);
  });

  return rows.join("\n");
};

const downloadCSV = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const PAGE_SIZE = 1000;

// Fetches a single page of history
const fetchHistoryPage = (
  page: number
): Promise<{ results: ISegmentationBlockHistoryEntry[]; count: number }> =>
  SegmentationBlocksHistoryApiService({
    page,
    page_size: PAGE_SIZE,
  });

// Fetches all history pages recursively
const fetchAllHistoryPages = async (): Promise<
  ISegmentationBlockHistoryEntry[]
> => {
  const allEntries: ISegmentationBlockHistoryEntry[] = [];

  // Fetch first page to get total count
  const firstPage = await fetchHistoryPage(1);
  if (!firstPage.results || firstPage.results.length === 0) {
    return allEntries;
  }

  allEntries.push(...firstPage.results);

  // Calculate how many more pages we need
  const totalPages = Math.ceil(firstPage.count / PAGE_SIZE);

  if (totalPages <= 1) {
    return allEntries;
  }

  // Fetch remaining pages in parallel
  const remainingPageNumbers = Array.from(
    { length: totalPages - 1 },
    (_, i) => i + 2
  );
  const remainingPages = await Promise.all(
    remainingPageNumbers.map((pageNum) => fetchHistoryPage(pageNum))
  );

  remainingPages.forEach((page) => {
    if (page.results) {
      allEntries.push(...page.results);
    }
  });

  return allEntries;
};

const ExportSegmentationBlocksHistoryButton: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  const handleExport = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch all history entries through pagination
      const allEntries = await fetchAllHistoryPages();

      if (allEntries.length > 0) {
        const csvContent = generateCSVContent(allEntries);
        const filename = `segmentation-blocks-history-${dayjs().format(
          "YYYY-MM-DD"
        )}.csv`;
        downloadCSV(csvContent, filename);

        showNotification({
          title: "Export Successful",
          message: `Exported ${allEntries.length} history entries to CSV.`,
          color: "green",
          autoClose: 4000,
        });
      } else {
        showNotification({
          title: "No Data",
          message: "No history entries to export.",
          color: "yellow",
          autoClose: 4000,
        });
      }
    } catch (err) {
      showNotification({
        title: "Export Failed",
        message: "Failed to export history. Please try again.",
        color: "red",
        autoClose: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <Tooltip label={isLoading ? "Exporting..." : "Export history"}>
      <ActionIcon
        variant="outline"
        size="lg"
        onClick={handleExport}
        loading={isLoading}
        data-testid="export-segmentation-blocks-history-button"
      >
        <Download size={18} />
      </ActionIcon>
    </Tooltip>
  );
};

export default ExportSegmentationBlocksHistoryButton;
